import { isEqual } from "lodash"
import { useCallback, useState } from "react"

/**
 * Draft state for a `SettingsPage` form. Save via `submit(run)`, where `run` resolves after the
 * mutation and the refetch of `saved`: the draft is dropped only then, since the server may
 * normalise values and a kept draft would stay dirty. A rejected `run` keeps the draft.
 */
export function useSettingsForm<T extends object>(saved: T) {
  const [draft, setDraft] = useState<T | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isDirty = draft !== null && !isEqual(draft, saved)
  const values = draft !== null && isDirty ? draft : saved

  const setValues = useCallback(
    (update: (current: T) => T) =>
      setDraft((prev) => update(prev !== null && !isEqual(prev, saved) ? prev : saved)),
    [saved],
  )
  const setField = useCallback(
    <K extends keyof T>(key: K, value: T[K]) =>
      setValues((current) => ({ ...current, [key]: value })),
    [setValues],
  )
  const reset = useCallback(() => setDraft(null), [])

  const submit = useCallback(
    async (run: (values: T) => Promise<unknown>) => {
      setIsSubmitting(true)
      try {
        await run(values)
        setDraft(null)
      } catch {
        // The mutation's onError reports the failure.
      } finally {
        setIsSubmitting(false)
      }
    },
    [values],
  )

  return { values, setField, setValues, reset, submit, isSubmitting, isDirty }
}
